import { useSelector } from 'react-redux'
import { Input, Carrousel, Modal, Textarea } from '@/components/ui'
import FacturationEntity from '@/components/shared/FacturationEntity'
import { formatDate, formatPrice } from '@/utils/formatters'

import { EyeIcon, BellSlashIcon } from '@heroicons/react/24/solid'
import UploadForm from '@/components/ui/UploadForm'

import { getFacturationEntiteValues, getInterventionStatusLabel, getMaterielStatusLabel, getFacturationStatusLabel, updateIntervention } from './interventionsSlice'
import Prestataire from '../prestataires/Show'
import { selectPrestataireById } from '../prestataires/prestatairesSlice'

export default ({ intervention, readOnly = false }) => {
  const prestataire = useSelector(state => selectPrestataireById(state, intervention.prestataire_id)) 
  const errors = useSelector((state) => state.interventions.requestStatus.errors)
  const isUpdating = useSelector((state) => state.interventions.loadingStatus.update)

  const { isOpen, handleShow, handleHide, dialogRef } = Modal.useModal()

  const facturationEntities = getFacturationEntiteValues(intervention)

  const DownloadButton = ({title, onClick}) => (
    <button 
      onClick={onClick}
      className="btn btn-xs btn-ghost btn-square p-1 hover:bg-primary-400"
      title={`Voir ${title}`} aria-label={`Voir ${title}`}
    >
      <EyeIcon/>
    </button>
  ) 

  return (
  <div className="bg-base-200 p-4">
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">

      <div className="flex flex-col pl-2 space-y-4">

        <div className='flex flex-col space-y-4'>
          <h4 className='text-gray-600 font-bold'>Info intervention :</h4>
          <Prestataire item={prestataire || intervention?.prestataire } />
          <Textarea.Display label="Détail de l'intervention" value={intervention?.details || ''}
            className='flex flex-col items-start'
          />
          <Textarea.Display label="Matériel à fournir par le client"
            value={intervention?.materiel_a_fournir|| ''}
            className='flex flex-col items-start'
          />
          <Input.Display label="Date de l'intervention" value={formatDate(intervention?.date_intervention || '')} />
        </div>

        <div className='flex flex-col space-y-4'>
          <h4 className='text-gray-600 font-bold'>Info Devis :</h4>
          <Input.Display label="Info rdv" value={intervention?.info_rdv || ''} valueClassName="uppercase" />
          <Input.DisplayFile 
            label="Devis" 
            title="le devis" 
            item={intervention} 
            name="devis"
            uploadFormComponent={<UploadForm 
              item={intervention}
              fileFieldName="devis"
              state={{
                errors, isUpdating,
                update: (data) => updateIntervention({
                  bien_id: intervention.bien_id,
                  id: intervention.id, 
                  data
                })
              }}
            />}
          /> 
        </div>

      </div>
        
      <div className="flex flex-col pl-2 space-y-4">
        <div className='flex flex-col space-y-4'>
          <h4 className='text-gray-600 font-bold'>Info facture :</h4>
          <Input.Display label="Montant chantier" value={formatPrice(intervention?.montant || '')} />
          <Input.Display label="Montant matériel" value={formatPrice(intervention?.montant_materiel || '')} />
          
          { intervention.facture_adresse ?
            <Input.Display label="Entité à facturer">
              <FacturationEntity entity={intervention.entite_facturable}
                entitiesList={facturationEntities}
                address={intervention.facture_adresse}
              />
            </Input.Display> :
            <Input.Display label="Entité à facturer" />
          }

          <Input.DisplayFile 
            label="Facture matériel" 
            title="la facture du matériel" 
            item={intervention} 
            name="facture_materiel" 
            uploadFormComponent={<UploadForm 
              item={intervention}
              fileFieldName="facture_materiel"
              state={{
                errors, isUpdating,
                update: (data) => updateIntervention({
                  bien_id: intervention.bien_id,
                  id: intervention.id, 
                  data
                })
              }}
            />}
          /> 

          <Input.DisplayFile 
            label="Facture acompte" title="la facture d'acompte" 
            item={intervention} 
            name="facture_acompte" 
            uploadFormComponent={<UploadForm 
              item={intervention}
              fileFieldName="facture_acompte"
              state={{
                errors, isUpdating,
                update: (data) => updateIntervention({
                  bien_id: intervention.bien_id,
                  id: intervention.id, 
                  data
                })
              }}
            />}
          /> 

          <Input.DisplayFile 
            label="Facture clôture" title="la facture de clôture" 
            item={intervention} name="facture_cloture" 
            uploadFormComponent={<UploadForm 
              item={intervention}
              fileFieldName="facture_cloture"
              state={{
                errors, isUpdating,
                update: (data) => updateIntervention({
                  bien_id: intervention.bien_id,
                  id: intervention.id, 
                  data
                })
              }}
            />}
          /> 
        </div>
          
      </div>

      <div className="flex flex-col pl-2 space-y-4">
        <div className="flex flex-col space-y-4">
          <h4 className='text-gray-600 font-bold'>Statuts :</h4>
          <Input.Display 
            label="Statut de l'intervention" 
            value={intervention?.statut ? 
              getInterventionStatusLabel(intervention.statut) : 
              '' 
            }
          />
          <Input.Display 
            label="Statut du matériel" 
            value={intervention?.statut_materiel ? 
              getMaterielStatusLabel(intervention.statut_materiel) : 
              ''
            } 
            />
          <Input.Display 
            label="Statut de la facturation" 
            value={intervention?.statut_facturation ?
              getFacturationStatusLabel(intervention.statut_facturation) : 
              ''
            } 
          />
        </div>

        {intervention.do_not_notify && (
          <div className="badge badge-warning gap-2 p-3">
            <BellSlashIcon className="w-4 h-4" />
            Notifications désactivées
          </div>
        )}

        <div className='flex flex-col space-y-4'>
          <h4 className='text-gray-600 font-bold'>Fin d'intervention :</h4>
          <Input.DisplayFile 
            label="PV de livraison" title="le PV de livraison" 
            item={intervention} 
            name="pv_livraison" 
            uploadFormComponent={<UploadForm 
              item={intervention}
              fileFieldName="pv_livraison"
              state={{
                errors, isUpdating,
                update: (data) => updateIntervention({
                  bien_id: intervention.bien_id,
                  id: intervention.id, 
                  data
                })
              }}
            />}
          /> 
          
          <div className="flex items-center justify-between">
            <Input.Display label="Photos">
              { intervention?.photos && intervention.photos.length > 0 &&                  
                <button title={`Voir les photos`} className="flex cursor-pointer" onClick={() => handleShow()}>
                  <span>{`${intervention.photos.length} photo(s)`}</span>
                </button>
              }
            </Input.Display>
            <div className="flex mb-2 ml-2 mr-4">
              { (!intervention?.photos || intervention.photos.length == 0) && !readOnly &&
                <UploadForm 
                  item={intervention} 
                  fileFieldName={"photos"} 
                  label="des photos" 
                  inputProps={{accept: "image/*", multiple: true}}  
                  state={{
                    errors, isUpdating,
                    update: (data) => updateIntervention({
                      bien_id: intervention.bien_id,
                      id: intervention.id, 
                      data
                    })
                  }}
                />
              }
              { (!!intervention?.photos && intervention.photos.length > 0) &&
                  <DownloadButton title="les photos" onClick={() => handleShow() } />
              }
            </div>
          </div>

        </div>

      </div>
    </div>

    <Modal
      open={isOpen}
      onClose={handleHide}
      ref={dialogRef}
      backdrop={true}
      className="max-w-full rounded-none"
    >
      <Modal.Header showCloseButton={true} />
      <Modal.Body>
        <Carrousel 
          images={intervention.photos?.map( photo => photo.url )} 
          indicatorType='numbers'
        />
      </Modal.Body>
    </Modal>   
  </div>
  )
}